// src/app/_components/space-picker/space-picker.component.ts
// Stage 2 UI (tasks/space_core_client.md §2): lists the user's Spaces, shows/switches the active
// one (governs which Space a *new* note is saved into — see SpaceContextService), and lets the
// user create a new SHARED Space. Content-only (no dialog chrome of its own), hosted inside a
// <p-dialog> by whichever page opens it (currently HomeComponent's user menu → "Spaces").

import { ChangeDetectionStrategy, ChangeDetectorRef, Component, OnDestroy, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { Router } from '@angular/router';
import { Subscription } from 'rxjs';
import { ButtonModule } from 'primeng/button';
import { ProgressSpinnerModule } from 'primeng/progressspinner';

import { Space, SpaceService } from '../../_service/space.service';
import { SpaceContextService } from '../../_service/space-context.service';

@Component({
  selector: 'app-space-picker',
  standalone: true,
  imports: [CommonModule, FormsModule, ButtonModule, ProgressSpinnerModule],
  templateUrl: './space-picker.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class SpacePickerComponent implements OnInit, OnDestroy {
  spaces: Space[] = [];
  activeSpaceId: string | null = null;
  loading = false;
  loadError: string | null = null;

  showCreateForm = false;
  newSpaceName = '';
  newSpaceDescription = '';
  creating = false;
  createError: string | null = null;

  private activeSpaceSub?: Subscription;

  constructor(
    private readonly spaceService: SpaceService,
    private readonly spaceContext: SpaceContextService,
    private readonly cdr: ChangeDetectorRef,
    private readonly router: Router,
  ) {}

  ngOnInit(): void {
    // OnPush: these are async sources outside any template-bound event, so each callback must
    // explicitly mark the component for check or its state change never reaches the DOM (the
    // component's own fields update fine either way — this is purely a rendering gap, which is
    // exactly why it doesn't show up in a unit test that inspects component state directly).
    this.activeSpaceSub = this.spaceContext.activeSpaceId$.subscribe((id) => {
      this.activeSpaceId = id;
      this.cdr.markForCheck();
    });
    this.loadSpaces();
  }

  ngOnDestroy(): void {
    this.activeSpaceSub?.unsubscribe();
  }

  loadSpaces(): void {
    this.loading = true;
    this.loadError = null;
    this.spaceService.getSpaces().subscribe({
      next: (spaces) => {
        // Own Space first, then alphabetical — the most likely one a user wants is their own.
        this.spaces = [...spaces].sort((a, b) => {
          if (a.type !== b.type) return a.type === 'PRIVATE' ? -1 : 1;
          return a.name.localeCompare(b.name);
        });
        this.loading = false;
        this.cdr.markForCheck();
      },
      error: (err) => {
        this.loadError = err?.message || 'Could not load Spaces.';
        this.loading = false;
        this.cdr.markForCheck();
      },
    });
  }

  selectSpace(space: Space): void {
    if (space.id === this.activeSpaceId) return;
    this.spaceContext.setActiveSpaceId(space.id);
  }

  /** Whether to show the Edit (administer) entry point for this Space. Restricted to SHARED
   * Spaces — every user is also OWNER of their own PRIVATE Space, but "administering" one
   * (renaming aside, and member management doesn't apply at all) isn't a feature this offers;
   * the server re-checks MANAGE_SPACE/MANAGE_MEMBERS regardless, so this is UX only. */
  canEditSpace(space: Space): boolean {
    return space.type === 'SHARED' && (space.current_user_role === 'OWNER' || space.current_user_role === 'ADMIN');
  }

  editSpace(event: Event, space: Space): void {
    event.stopPropagation();
    this.router.navigate(['/space-admin', space.id]);
  }

  startCreate(): void {
    this.showCreateForm = true;
    this.createError = null;
  }

  cancelCreate(): void {
    this.showCreateForm = false;
    this.newSpaceName = '';
    this.newSpaceDescription = '';
    this.createError = null;
  }

  submitCreate(): void {
    const name = this.newSpaceName.trim();
    if (!name || this.creating) return;

    this.creating = true;
    this.createError = null;
    const description = this.newSpaceDescription.trim();

    this.spaceService.createSpace(name, description || undefined).subscribe({
      next: (space) => {
        this.spaces = [...this.spaces, space].sort((a, b) => {
          if (a.type !== b.type) return a.type === 'PRIVATE' ? -1 : 1;
          return a.name.localeCompare(b.name);
        });
        // The creator becomes its OWNER — make it the active Space right away so the natural next
        // action (adding a note to the Space just created) works without a second switch.
        this.spaceContext.setActiveSpaceId(space.id);
        this.creating = false;
        this.cancelCreate();
        this.cdr.markForCheck();
      },
      error: (err) => {
        this.createError = err?.message || 'Could not create the Space.';
        this.creating = false;
        this.cdr.markForCheck();
      },
    });
  }

  trackBySpaceId(_index: number, space: Space): string {
    return space.id;
  }
}
